/* eslint-disable react/no-unstable-nested-components */
import React, { memo, useEffect, useState } from "react";
import { Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import axios from "axios";
import { CustomInputWithController } from "../../Core/Components/CustomInput";
import Button from "../../Core/Components/CustomButton";
import { updateLoggedInUser } from "../../Redux/Slices/loggedInUser.slice";
import BlockedEyeIcon from "../../Common/Svgs/BlockedEyeIcon";
import ToastMessageModal from "./ToastMessageModal";
import ArrowIcon from "../../Common/Svgs/ArrowIcon";
import { PERMISSIONS } from "./constants";

const validationForEmail = {
  required: "please enter email address",
  pattern: {
    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
    message: "Enter a vaild email",
  },
};

const validationForPassword = {
  required: "please enter password",
  minLength: {
    value: 8,
    message: "less characters for password",
  },
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { handleSubmit, control, formState, setError, getValues } = useForm({
    email: "",
    password: "",
  });

  const localPassword = getValues("password");

  const [showPassword, setShowPassword] = useState(false);
  const [loginFocus, setLoginFocus] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [tenantError, setTenantError] = useState({
    visible: false,
    description: "",
  });

  const onSubmit = async (e, isMobile = false) => {
    const { email, password } = e;

    try {
      dispatch(
        updateLoggedInUser({
          key: "loading",
          value: true,
        }),
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        { email, password },
      );

      if (response.data.token) {
        const { message, token, ...rest } = response.data;

        if (isMobile) {
          // user permissions
          const permissions = rest?.permissions || [];

          // check if user has permission for mobile app view and edit
          // else -> no permission modal
          if (permissions?.includes(PERMISSIONS.APP_VIEW_AND_EDIT)) {
            localStorage.setItem("token", token);
            navigate("/download");
          } else {
            setTenantError({
              visible: true,
              description: "You do not have mobile app permission.",
            });
          }
        } else {
          localStorage.setItem("token", token);
          dispatch(updateLoggedInUser({ key: "userDetails", value: rest }));
          navigate("/home");
        }
      }
    } catch (error) {
      setError(error.response.data.type, {
        type: "server",
        message: error.response.data.message,
      });

      if (error.response.data.message === "Access denied") {
        const description =
          error?.response?.data?.description || "The tenant has been deleted.";

        setTenantError({ visible: true, description });
      }
    } finally {
      dispatch(
        updateLoggedInUser({
          key: "loading",
          value: false,
        }),
      );
    }
  };

  // close the tenant error toast message
  useEffect(() => {
    let timeout;
    if (tenantError) {
      timeout = setTimeout(() => {
        setTenantError({ visible: false, description: "" });
      }, 5000);
    }

    return () => clearTimeout(timeout);
  }, [tenantError]);

  return (
    <div>
      <div className="hidden lg:block w-screen h-screen">
        <Row className="h-full">
          <Col span={15} className="h-full overflow-hidden">
            <img
              src="/assets/images/Login/login-image-new.png"
              alt="LoginImage"
              className="object-cover w-full h-full"
            />
          </Col>

          <Col span={9}>
            <div className="flex flex-col items-center justify-center w-full h-full gap-y-6 font-poppins bg-background-white">
              <img
                // src="/assets/songa-main-logo.svg"
                src="/assets/songa-main-logo-4x.png"
                alt="Songa Mobility"
                className="w-[258px] h-14"
              />

              <div>
                <div className="text-xs font-light leading-4.5 text-center text-dark-gray">
                  Login to continue to dashboard
                </div>
              </div>

              <form
                onSubmit={handleSubmit((data) => onSubmit(data, false))}
                className="flex flex-col items-center justify-center w-full gap-y-6"
              >
                <CustomInputWithController
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={loginFocus}
                  setFocus={setLoginFocus}
                  name="email"
                  placeholder="Email Address"
                  rules={validationForEmail}
                />

                <CustomInputWithController
                  inputType={showPassword ? "text" : "password"}
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={passwordFocus}
                  setFocus={setPasswordFocus}
                  name="password"
                  placeholder="Password"
                  rules={validationForPassword}
                  suffix={
                    <button
                      type="button"
                      onClick={() => {
                        if (localPassword) {
                          setShowPassword(!showPassword);
                        }
                      }}
                      disabled={!localPassword}
                    >
                      {showPassword ? (
                        <img src="/images/eye.png" className="w-5 h-5" alt="" />
                      ) : (
                        <BlockedEyeIcon
                          className={
                            localPassword ? "text-black" : "text-dark-gray"
                          }
                        />
                      )}
                    </button>
                  }
                />

                <Button type="submit" width="login">
                  Login
                </Button>

                <div className="flex justify-between w-[312px] mt-4">
                  <p className="font-poppins font-medium text-sm w-fit">
                    Don’t have an account?
                  </p>

                  <div
                    className="flex items-center justify-center gap-2 cursor-pointer"
                    onClick={() => navigate("/sign-up")}
                  >
                    <p className="font-poppins font-medium text-sm leading-none text-green underline">
                      Sign Up
                    </p>
                    <ArrowIcon className="rotate-90 text-green" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/reset-password")}
                  className="cursor-pointer font-poppins font-light text-xs leading-4.5 text-dark-gray hover:text-black hover:underline"
                >
                  Change Password
                </button>
              </form>
            </div>
          </Col>
        </Row>
      </div>

      <div className="lg:hidden w-screen h-screen flex flex-col items-start justify-start font-poppins bg-background-white">
        <div className="w-full h-[259px] relative">
          <img
            src="/assets/images/Login/login-image-new.png"
            alt="LoginImage"
            className="object-cover w-full h-full"
          />
        </div>

        <div className="mt-6 flex flex-col items-center w-full h-full gap-y-6 font-poppins bg-background-white">
          <img
            // src="/assets/songa-main-logo.svg"
            src="/assets/songa-main-logo-4x.png"
            alt="Songa Mobility"
            className="w-[258px] h-14"
          />

          <div>
            <div className="text-xs font-light leading-4.5 text-center text-dark-gray">
              Login to continue to dashboard
            </div>
          </div>

          <form
            onSubmit={handleSubmit((data) => onSubmit(data, true))}
            className="flex flex-col items-center w-full gap-y-6 px-4"
          >
            <div className="w-full">
              <CustomInputWithController
                intent="mobile"
                control={control}
                formState={formState}
                focus={loginFocus}
                setFocus={setLoginFocus}
                name="email"
                placeholder="Email Address"
                rules={validationForEmail}
              />
            </div>

            <div className="w-full">
              <CustomInputWithController
                inputType={showPassword ? "text" : "password"}
                intent="mobile"
                control={control}
                formState={formState}
                focus={passwordFocus}
                setFocus={setPasswordFocus}
                name="password"
                placeholder="Password"
                rules={validationForPassword}
                suffix={
                  <button
                    type="button"
                    onClick={() => {
                      if (localPassword) {
                        setShowPassword(!showPassword);
                      }
                    }}
                    disabled={!localPassword}
                  >
                    {showPassword ? (
                      <img src="/images/eye.png" className="w-5 h-5" alt="" />
                    ) : (
                      <BlockedEyeIcon
                        className={
                          localPassword ? "text-black" : "text-dark-gray"
                        }
                      />
                    )}
                  </button>
                }
              />
            </div>

            <Button type="submit" width="full">
              Login
            </Button>

            <div className="flex justify-between w-full">
              <p className="font-poppins font-medium text-sm w-fit">
                Don’t have an account?
              </p>

              <div
                className="flex items-center justify-center gap-2 cursor-pointer"
                onClick={() => navigate("/sign-up")}
              >
                <p className="font-poppins font-medium text-sm leading-none text-green underline">
                  Sign Up
                </p>
                <ArrowIcon className="rotate-90 text-green" />
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/reset-password")}
              className="cursor-pointer font-poppins font-light text-xs leading-4.5 text-dark-gray hover:text-black hover:underline"
            >
              Change Password
            </button>
          </form>
        </div>
      </div>

      {tenantError?.visible ? (
        <ToastMessageModal
          visible={tenantError?.visible}
          onCancel={() => setTenantError({ visible: false, description: "" })}
          title="Access Denied"
          description={tenantError?.description}
        />
      ) : null}
    </div>
  );
};
export default memo(Login);
